import React, { useEffect } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Grid, Chip,
  Stack, Button, Divider, List, ListItem, ListItemIcon, ListItemText,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import {
  Check, LaptopMac, ExpandMore, Receipt, Euro,
  AssignmentInd, Verified, ArrowForward
} from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';

const LogicielFactureAutoEntrepreneur = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Logiciel facture auto-entrepreneur 2026 | Facteio';

    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    const setProp = (prop, content) => {
      let el = document.querySelector(`meta[property="${prop}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('property', prop); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Logiciel facture auto-entrepreneur simple et conforme. Mention TVA non applicable (art. 293 B CGI), numérotation automatique, PDF en 1 clic. Prêt pour 2026.');
    setMeta('keywords', 'logiciel facture auto-entrepreneur, facture auto-entrepreneur, mention TVA non applicable, logiciel micro-entrepreneur, facturation auto-entrepreneur 2026');
    setProp('og:title', 'Logiciel facture auto-entrepreneur | Facteio');
    setProp('og:description', 'Facteio : logiciel facture auto-entrepreneur. Toutes les mentions obligatoires, numérotation automatique, conforme 2026.');

    let sd = document.getElementById('sd-ae');
    if (!sd) {
      sd = document.createElement('script');
      sd.id = 'sd-ae';
      sd.type = 'application/ld+json';
      document.head.appendChild(sd);
    }
    sd.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Facteio",
      "applicationCategory": "BusinessApplication",
      "description": "Logiciel facture auto-entrepreneur. Mentions obligatoires, numérotation automatique, conformité 2026.",
      "url": "https://facteio.com/logiciel-facture-auto-entrepreneur",
      "offers": { "@type": "Offer", "priceCurrency": "EUR", "price": "0", "description": "Essai gratuit" }
    });

    return () => { const el = document.getElementById('sd-ae'); if (el) el.remove(); };
  }, []);

  const goToRegisterTracked = () => {
    const params = new URLSearchParams({
      utm_source: 'site_facteio', utm_medium: 'cta',
      utm_campaign: 'logiciel_facture_auto_entrepreneur', utm_content: 'cta_page'
    });
    navigate(`/register?${params.toString()}`);
  };

  return (
    <Box sx={(theme) => ({ background: theme.palette.background.default, minHeight: '100vh' })}>

      {/* Hero */}
      <Box sx={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5986 60%, #3a7abf 100%)', color: '#fff', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Button component={Link} to="/" sx={{ color: 'rgba(255,255,255,0.7)', mb: 3, fontWeight: 600, '&:hover': { color: '#fff' } }}>
            ← Retour à l'accueil
          </Button>

          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} mb={3}>
            <Chip icon={<LaptopMac sx={{ color: '#fff !important' }} />} label="Auto-entrepreneur" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 700 }} />
            <Chip icon={<Check sx={{ color: '#fff !important' }} />} label="Micro-entrepreneur" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' }} />
            <Chip icon={<Check sx={{ color: '#fff !important' }} />} label="Mentions légales auto" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' }} />
            <Chip icon={<Check sx={{ color: '#fff !important' }} />} label="Conforme 2026" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' }} />
          </Stack>

          <Typography variant="h1" sx={{ fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 900, lineHeight: 1.15, mb: 2 }}>
            Logiciel facture auto-entrepreneur simple et conforme
          </Typography>
          <Typography variant="h2" sx={{ fontSize: { xs: '1.1rem', md: '1.4rem' }, fontWeight: 400, opacity: 0.85, mb: 4, maxWidth: 700 }}>
            Créez vos factures et devis en 2 minutes. Toutes les mentions obligatoires pour les auto-entrepreneurs
            sont intégrées — dont la mention TVA non applicable (art. 293 B du CGI). Prêt pour la réforme 2026.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              onClick={goToRegisterTracked}
              sx={{ bgcolor: '#977F6B', '&:hover': { bgcolor: '#7A6358' }, borderRadius: 50, px: 5, py: 1.8, fontSize: '1.05rem', fontWeight: 700 }}
            >
              Essayer gratuitement
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/pricing')}
              sx={{ borderColor: 'rgba(255,255,255,0.5)', color: '#fff', borderRadius: 50, px: 4, py: 1.8, fontWeight: 600 }}
            >
              Voir les tarifs
            </Button>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>

        {/* Ce que la loi exige pour un auto-entrepreneur */}
        <Box mb={8}>
          <Typography variant="h3" fontWeight={800} mb={1}>
            Vos mentions obligatoires, gérées automatiquement
          </Typography>
          <Typography color="text.secondary" mb={5} sx={{ maxWidth: 680 }}>
            En tant qu'auto-entrepreneur (micro-entrepreneur), vos factures doivent comporter des mentions spécifiques.
            Facteio les insère automatiquement — vous n'avez rien à mémoriser.
          </Typography>

          <Grid container spacing={3}>
            {[
              {
                icon: <AssignmentInd sx={{ color: '#977F6B', fontSize: 32 }} />,
                title: '"TVA non applicable, art. 293 B du CGI"',
                text: 'La mention la plus importante pour un auto-entrepreneur non assujetti à la TVA. Elle est ajoutée automatiquement sur chaque facture dès que vous activez le régime micro.',
                highlight: true
              },
              {
                icon: <Receipt sx={{ color: '#977F6B', fontSize: 32 }} />,
                title: 'Numérotation chronologique obligatoire',
                text: 'Vos factures sont numérotées automatiquement (ex : F-2026-001) dans un ordre chronologique strict. En cas de contrôle fiscal, votre numérotation est irréprochable.',
              },
              {
                icon: <Verified sx={{ color: '#977F6B', fontSize: 32 }} />,
                title: 'SIRET + code APE automatiques',
                text: 'Votre numéro SIRET et votre code APE (activité principale exercée) sont affichés sur chaque document. Obligatoires pour les auto-entrepreneurs.',
              },
              {
                icon: <Euro sx={{ color: '#977F6B', fontSize: 32 }} />,
                title: 'Seuils de chiffre d\'affaires visibles',
                text: 'Optionnellement, affichez une alerte interne quand vous approchez des seuils de la micro-entreprise (77 700 € services / 188 700 € ventes) pour anticiper le changement de régime.',
              },
            ].map((item, i) => (
              <Grid item xs={12} md={6} key={i}>
                <Card sx={{ height: '100%', border: item.highlight ? '2px solid #977F6B' : '1px solid #E5E7EB', borderRadius: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="flex-start" mb={1.5}>
                      {item.icon}
                      <Box>
                        <Typography variant="h6" fontWeight={700} mb={0.5} sx={item.highlight ? { color: '#977F6B' } : {}}>
                          {item.title}
                        </Typography>
                        <Typography color="text.secondary">{item.text}</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ mb: 8 }} />

        {/* Simplicité */}
        <Box mb={8}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" fontWeight={800} mb={2}>
                Facturer ne devrait pas prendre plus de 2 minutes
              </Typography>
              <Typography color="text.secondary" mb={3}>
                En tant qu'auto-entrepreneur, vous avez déjà beaucoup à gérer. Votre logiciel de facturation
                doit aller à l'essentiel — sans jargon, sans complexité inutile.
              </Typography>
              <List>
                {[
                  'Client enregistré en 30 secondes',
                  'Facture créée depuis un modèle pré-rempli',
                  'PDF généré et envoyable par email en 1 clic',
                  'Historique de toutes vos factures consultable partout',
                  'Aucun logiciel à installer — 100 % en ligne',
                ].map((t, i) => (
                  <ListItem key={i} disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}><Check sx={{ color: '#977F6B' }} /></ListItemIcon>
                    <ListItemText primary={t} />
                  </ListItem>
                ))}
              </List>
              <Button
                variant="contained"
                endIcon={<ArrowForward />}
                onClick={goToRegisterTracked}
                sx={{ bgcolor: '#977F6B', '&:hover': { bgcolor: '#7A6358' }, borderRadius: 50, px: 4, py: 1.5, mt: 2, fontWeight: 700 }}
              >
                Essayer gratuitement
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ bgcolor: '#F9FAFB', borderRadius: 3, p: 4, border: '1px solid #E5E7EB' }}>
                <Typography fontWeight={700} mb={3} color="text.secondary" fontSize="0.85rem" textTransform="uppercase" letterSpacing={1}>
                  Exemple de facture auto-entrepreneur
                </Typography>
                {[
                  { label: 'Votre nom / raison sociale', value: 'Jean Martin – Auto-entrepreneur' },
                  { label: 'SIRET', value: '123 456 789 00012' },
                  { label: 'Code APE', value: '6201Z' },
                  { label: 'Numéro de facture', value: 'F-2026-042' },
                  { label: 'Montant HT', value: '850,00 €' },
                  { label: 'TVA', value: 'TVA non applicable, art. 293 B du CGI', bold: true, color: '#977F6B' },
                  { label: 'Montant TTC', value: '850,00 €' },
                ].map((row, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: i < 6 ? '1px solid #E5E7EB' : 'none' }}>
                    <Typography color="text.secondary" fontSize="0.9rem">{row.label}</Typography>
                    <Typography fontWeight={row.bold ? 700 : 500} color={row.color || 'text.primary'} fontSize="0.9rem">{row.value}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ mb: 8 }} />

        {/* Réforme 2026 */}
        <Box mb={8}>
          <Card sx={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5986 100%)', color: '#fff', borderRadius: 3, p: { xs: 3, md: 5 } }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={8}>
                <Chip label="Réforme 2026" sx={{ bgcolor: '#977F6B', color: '#fff', fontWeight: 700, mb: 2 }} />
                <Typography variant="h4" fontWeight={800} mb={2}>
                  Auto-entrepreneurs : êtes-vous prêt pour 2026 ?
                </Typography>
                <Typography sx={{ opacity: 0.85, mb: 2 }}>
                  La réforme de la facturation électronique s'applique également aux auto-entrepreneurs
                  pour leurs transactions BtoB. À partir du <strong>1er septembre 2026</strong>,
                  vos factures à d'autres professionnels devront être émises au format électronique.
                  Facteio vous y prépare automatiquement.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/reforme-2026')}
                  sx={{ bgcolor: '#977F6B', '&:hover': { bgcolor: '#7A6358' }, borderRadius: 50, px: 4, py: 1.5, fontWeight: 700 }}
                >
                  Comprendre la réforme
                </Button>
              </Grid>
            </Grid>
          </Card>
        </Box>

        {/* FAQ */}
        <Box mb={8}>
          <Typography variant="h3" fontWeight={800} mb={4}>
            Questions fréquentes — Auto-entrepreneur
          </Typography>
          {[
            { q: 'Quel logiciel de facturation pour un auto-entrepreneur ?', a: 'Facteio est idéal pour les auto-entrepreneurs (micro-entrepreneurs) : il gère automatiquement la mention "TVA non applicable (art. 293 B du CGI)", la numérotation chronologique obligatoire, et génère des PDF prêts à envoyer. Gratuit pour démarrer.' },
            { q: 'Doit-on mettre "TVA non applicable" sur une facture auto-entrepreneur ?', a: 'Oui, c\'est obligatoire si vous n\'êtes pas redevable de la TVA (régime de franchise en base, art. 293 B du CGI). Cette mention indique à votre client qu\'aucune TVA n\'est facturée. Facteio l\'insère automatiquement.' },
            { q: 'Comment numéroter les factures en auto-entrepreneur ?', a: 'La loi impose une numérotation chronologique et continue, sans rupture ni doublon. Exemple : F-2026-001, F-2026-002, etc. Facteio génère cette numérotation automatiquement pour chaque facture émise.' },
            { q: 'Un auto-entrepreneur peut-il faire des devis ?', a: 'Oui, et c\'est même recommandé pour les prestations importantes. Facteio vous permet de créer un devis professionnel signable, puis de le transformer en facture en un clic une fois accepté.' },
            { q: 'La facture électronique est-elle obligatoire pour les auto-entrepreneurs en 2026 ?', a: 'Oui, pour les transactions entre professionnels (BtoB). À partir du 1er septembre 2026, les auto-entrepreneurs devront recevoir des factures électroniques et émettre les leurs au format requis pour les clients professionnels.' },
          ].map((faq, i) => (
            <Accordion key={i} sx={{ mb: 1, border: '1px solid #E5E7EB', borderRadius: '12px !important', '&:before': { display: 'none' }, boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ '& .MuiAccordionSummary-content': { fontWeight: 700 } }}>
                {faq.q}
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Liens internes */}
        <Box mb={8}>
          <Typography variant="h5" fontWeight={700} mb={3}>Vous cherchez aussi :</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" gap={2}>
            <Button variant="outlined" component={Link} to="/logiciel-devis-facture-artisan" sx={{ borderRadius: 50, fontWeight: 600 }}>
              Logiciel devis facture artisan →
            </Button>
            <Button variant="outlined" component={Link} to="/logiciel-facture-pme" sx={{ borderRadius: 50, fontWeight: 600 }}>
              Logiciel facture PME →
            </Button>
            <Button variant="outlined" component={Link} to="/reforme-2026" sx={{ borderRadius: 50, fontWeight: 600 }}>
              Réforme facture électronique 2026 →
            </Button>
          </Stack>
        </Box>

        {/* CTA final */}
        <Card sx={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5986 100%)', color: '#fff', borderRadius: 3, p: { xs: 3, md: 5 }, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={800} mb={1}>
            Facturez proprement, dès aujourd'hui
          </Typography>
          <Typography sx={{ opacity: 0.9, mb: 3, maxWidth: 520, mx: 'auto' }}>
            Toutes vos mentions légales, votre numérotation et vos PDF — en 2 minutes.
            Essayez Facteio gratuitement, sans carte bancaire.
          </Typography>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForward />}
            onClick={goToRegisterTracked}
            sx={{ bgcolor: '#977F6B', '&:hover': { bgcolor: '#7A6358' }, borderRadius: 50, px: 5, py: 1.8, fontWeight: 700 }}
          >
            Créer mon compte gratuit
          </Button>
        </Card>

      </Container>
    </Box>
  );
};

export default LogicielFactureAutoEntrepreneur;
